// ** React Imports
import { useState, SyntheticEvent, Fragment } from 'react'

// ** Next Import
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';

// ** MUI Imports
import Box from '@mui/material/Box'
import Menu from '@mui/material/Menu'
import Badge from '@mui/material/Badge'
import Avatar from '@mui/material/Avatar'
import Divider from '@mui/material/Divider'
import MenuItem from '@mui/material/MenuItem'
import { styled } from '@mui/material/styles'
import Typography from '@mui/material/Typography'
import InputLabel from '@mui/material/InputLabel';
import FormControl from '@mui/material/FormControl';
import Select, { SelectChangeEvent } from '@mui/material/Select';

// ** Icons Imports
import CogOutline from 'mdi-material-ui/CogOutline'
import CurrencyUsd from 'mdi-material-ui/CurrencyUsd'
import EmailOutline from 'mdi-material-ui/EmailOutline'
import LogoutVariant from 'mdi-material-ui/LogoutVariant'
import AccountOutline from 'mdi-material-ui/AccountOutline'
import MessageOutline from 'mdi-material-ui/MessageOutline'
import HelpCircleOutline from 'mdi-material-ui/HelpCircleOutline'

// ** Context
import { useAuth } from 'src/hooks/useAuth'

// ** Type Imports
import { Settings } from 'src/materio/@core/context/settingsContext'
import ApplicationStore from '../../../stores/ApplicationStore';
import { client } from '../../../utils/axios-client';
import { changeRoleEndpoint } from '../../../configs/endpoints';



interface Props {
    settings: Settings
}

// ** Styled Components
const BadgeContentSpan = styled('span')(({ theme }) => ({
    width: 8,
    height: 8,
    borderRadius: '50%',
    backgroundColor: theme.palette.success.main,
    boxShadow: `0 0 0 2px ${theme.palette.background.paper}`
}))

const UserDropdown = (props: Props) => {    
    // ** Props
    const { settings } = props

    // ** States
    const [anchorEl, setAnchorEl] = useState<Element | null>(null)

    // ** Hooks
    const navigate = useNavigate()
    const { logout, user } = useAuth()

    // ** Vars
    const { direction } = settings

    const handleDropdownOpen = (event: SyntheticEvent) => {
        setAnchorEl(event.currentTarget)
    }

    const handleDropdownClose = (url?: string) => {
        if (url) {
            navigate(url)
        }
        setAnchorEl(null)
    }

    const styles = {
        py: 2,
        px: 4,
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        color: 'text.primary',
        textDecoration: 'none',
        '& svg': {
            fontSize: '1.375rem',
            color: 'text.secondary'
        }
    }

    const handleLogout = () => {
        logout()
        handleDropdownClose()
    }

    const onRoleChange = async (roleId: string) => {
        try {
            const response = await client.post(changeRoleEndpoint.replace('{id}', roleId))

            if (response.data.succeeded) {
                window.location.reload();
            } else {
                ApplicationStore.update(s => {
                    s.snackbarMessage = 'Error changing roles';
                    s.snackbarOpen = true;
                })
            }
        } catch (err) {
            ApplicationStore.update(s => {
                s.snackbarMessage = 'Error changing roles';
                s.snackbarOpen = true;
            })
        }
    }

    return (
        <Fragment>
            <Badge
                overlap='circular'
                onClick={handleDropdownOpen}
                sx={{ ml: 2, cursor: 'pointer' }}
                badgeContent={<BadgeContentSpan />}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right'
                }}
            >
                <Avatar
                    alt={user?.fullName }
                    onClick={handleDropdownOpen}
                    sx={{ width: 40, height: 40 }}
                    src={user?.avatarFileName ? `/api/admin/users/images/${user.avatarFileName}` : '/images/avatars/1.png'}
                />
            </Badge>
            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={() => handleDropdownClose()}
                sx={{ '& .MuiMenu-paper': { width: 230, mt: 4 } }}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: direction === 'ltr' ? 'right' : 'left'
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: direction === 'ltr' ? 'right' : 'left'
                }}
            >
                <Box sx={{ pt: 2, pb: 3, px: 4 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Badge
                            overlap='circular'
                            badgeContent={<BadgeContentSpan />}
                            anchorOrigin={{
                                vertical: 'bottom',
                                horizontal: 'right'
                            }}
                        >
                            <Avatar
                                alt={user?.fullName}
                                src={user?.avatar ? user.avatar : '/images/avatars/1.png'}
                                sx={{ width: '2.5rem', height: '2.5rem' }}
                            />
                        </Badge>
                        <Box
                            sx={{
                                ml: 3,
                                display: 'flex',
                                alignItems: 'flex-start',
                                flexDirection: 'column'
                            }}
                        >
                            <Typography sx={{ fontWeight: 600 }}>{user?.fullName }</Typography>
                            <FormControl fullWidth size='small'>
                                <Select
                                    value={user?.roleId}
                                    onChange={(e) => { onRoleChange(e.target.value) }}
                                    size='small'
                                >
                                    {user?.roles.map(role => <MenuItem key={role.id} value={role.id}>{role.name}</MenuItem>)}
                                </Select>
                            </FormControl>

                        </Box>
                    </Box>
                </Box>
                <Divider sx={{ mt: 0, mb: 1 }} />
                <MenuItem sx={{ p: 0 }} onClick={() => handleDropdownClose()}>
                    <Link to={`/account-settings/${user?.id}`}>
                        <Box sx={styles}>

                            <AccountOutline sx={{ mr: 2 }} />
                            Profile
                        </Box>
                    </Link>
                </MenuItem>
                <Divider />
                <MenuItem sx={{ py: 2 }} onClick={handleLogout}>
                    <LogoutVariant
                        sx={{
                            mr: 2,
                            fontSize: '1.375rem',
                            color: 'text.secondary'
                        }}
                    />
                    Logout
                </MenuItem>
            </Menu>
        </Fragment>
    )
}

export default UserDropdown
